import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lightning, CheckCircle, PawPrint, MagicWand } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const S = "https://static.prod-images.emergentagent.com/jobs/062e260e-b0f9-432f-a759-5890d554bb91/images/";

const CARS = [
  { id: "gwagon", name: "G-Wagon", wraps: {
    camo: S + "ce45362197ddcb0caf43861da8e72ce20367e530160a579321fec77b5be0c8f4.jpeg",
    aquapink: S + "c2c6301e2d4da91a22efac58ef9886015c46f8e2dfb5f75c5d44fefdd25d7b63.jpeg",
    lime: S + "da7576c168dfee86b731347fe3a3ca1d5cc61a680a95219b64d157ed3910682b.jpeg",
  } },
  { id: "muscle", name: "Muscle Car", wraps: {
    camo: S + "c8d1486265e36503f8b398898261394b8ee40815ddd083a608422d43ff2f43b5.jpeg",
    aquapink: S + "bce173a44f5efc857225d8c9e3eb9d239994672c3402cffb1d2121bb74e73096.jpeg",
    lime: S + "f24bb1fb53cd0fb8a513d88c85e1983eb1c7246461f5af4f8e7563a18ffad72d.jpeg",
  } },
];

const WRAPS = [
  { id: "camo", name: "Neon Pit Bull Camo", chip: "linear-gradient(135deg,#ff1493,#00e5e5,#b6ff00)", price: 8500, blurb: "Our signature. A pit bull stalks the pattern — hidden in plain sight." },
  { id: "aquapink", name: "Aqua / Pink Hypercolor", chip: "linear-gradient(135deg,#00e5e5,#ff1493)", price: 7500, blurb: "Miami-night gradient. Loud, clean, unmissable." },
  { id: "lime", name: "Lime Reflective", chip: "linear-gradient(135deg,#b6ff00,#0a0a0a)", price: 7900, blurb: "Toxic lime with a reflective bite that hits under headlights." },
];

const SERVICES = [
  { title: "Full Vehicle Wraps", desc: "Complete color-change and custom graphic wraps in premium cast vinyl." },
  { title: "Hidden-Pit Design", desc: "Our signature: a camouflaged pit bull baked into every pattern. Only the pack notices." },
  { title: "Commercial Fleet", desc: "Branded fleet wraps that turn every vehicle into a moving billboard for the mission." },
];

export default function Wrap() {
  const [car, setCar] = useState("gwagon");
  const [wrap, setWrap] = useState("camo");
  const [reveal, setReveal] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", vehicle: "", message: "" });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const inquiryRef = useRef(null);

  const carObj = CARS.find((c) => c.id === car);
  const wrapObj = WRAPS.find((w) => w.id === wrap);
  const img = carObj.wraps[wrap];

  const bookThis = () => {
    setForm((f) => ({ ...f, vehicle: carObj.name, message: `I want the ${wrapObj.name} wrap on my ${carObj.name}.` }));
    inquiryRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/inquiries", {
        name: form.name, email: form.email, type: "wrap",
        subject: `Neon Wigglez Wrap — ${wrapObj.name}`,
        message: form.message, details: { vehicle: form.vehicle, wrap: wrapObj.name, quote: wrapObj.price },
      });
      setDone(true);
      toast.success("Inquiry sent — the wrap crew will reach out.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="wrap-page" className="bg-background">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-6 text-center">
        <Lightning weight="fill" size={34} className="neon-text mx-auto mb-4" />
        <p className="overline neon-text mb-3">Neon Wigglez Wrap</p>
        <h1 className="font-display text-5xl sm:text-6xl tracking-tight leading-[0.95]">Wrap it <span className="neon-text">loud.</span></h1>
        <p className="mt-4 text-muted-foreground max-w-xl mx-auto">Pick your ride, flip the colorway, and find the hidden pit. Then book the crew.</p>
      </section>

      {/* Customizer */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-16" data-testid="wrap-customizer">
        <div className="rounded-2xl border border-primary/25 bg-card overflow-hidden neon-glow">
          <div className="relative aspect-video bg-black">
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.img key={car + wrap} src={img} alt={`${carObj.name} — ${wrapObj.name}`}
                initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }} className="absolute inset-0 h-full w-full object-cover" data-testid="wrap-preview-image" />
            </AnimatePresence>

            {/* hidden-pit reveal */}
            <AnimatePresence>
              {reveal && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 pointer-events-none" data-testid="wrap-reveal-overlay">
                  <div className="absolute inset-0 bg-black/40" />
                  <div className="absolute left-1/2 top-[52%] -translate-x-1/2 -translate-y-1/2 h-40 w-40 rounded-full border-2 border-primary" style={{ boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)" }} />
                  <span className="absolute left-1/2 top-[52%] mt-24 -translate-x-1/2 overline text-primary bg-black/70 px-3 py-1.5 rounded-full flex items-center gap-1.5"><PawPrint weight="fill" size={13} /> there he is — the hidden pit</span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* big price tag */}
            <div className="absolute top-4 right-4 text-right">
              <span className="overline text-white/70 block">Starting at</span>
              <span className="font-display text-3xl sm:text-4xl neon-text" data-testid="wrap-price">${wrapObj.price.toLocaleString()}</span>
            </div>

            <button onClick={() => setReveal((r) => !r)} className="absolute bottom-4 left-4 inline-flex items-center gap-2 overline px-4 py-2 rounded-full bg-black/60 backdrop-blur border border-primary/40 text-primary hover:bg-black/80 transition-colors" data-testid="wrap-reveal-toggle">
              <MagicWand weight="fill" size={14} /> {reveal ? "Hide the pit" : "Find the hidden pit"}
            </button>
          </div>

          <div className="p-6 sm:p-8">
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <p className="overline text-muted-foreground mb-3">Choose your ride</p>
                <div className="flex gap-3">
                  {CARS.map((c) => (
                    <button key={c.id} onClick={() => setCar(c.id)} data-testid={`wrap-car-${c.id}`}
                      className={`px-5 py-2.5 rounded-[10px] font-modern border transition-colors ${car === c.id ? "bg-primary text-primary-foreground border-primary" : "border-white/20 text-white hover:border-primary/50"}`}>{c.name}</button>
                  ))}
                </div>
              </div>
              <div>
                <p className="overline text-muted-foreground mb-3">Choose your wrap</p>
                <div className="flex gap-3 flex-wrap">
                  {WRAPS.map((w) => (
                    <button key={w.id} onClick={() => setWrap(w.id)} title={w.name} data-testid={`wrap-swatch-${w.id}`}
                      className={`h-10 w-10 rounded-full border-2 transition-transform hover:scale-110 ${wrap === w.id ? "border-white scale-110" : "border-white/25"}`} style={{ background: w.chip }} />
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="font-modern text-xl text-white">{wrapObj.name}</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-md">{wrapObj.blurb}</p>
              </div>
              <button onClick={bookThis} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-7 py-3.5 rounded-[10px] font-modern font-medium hover:bg-primary/90 transition-colors shrink-0" data-testid="wrap-book-cta">
                <Lightning weight="fill" size={18} /> Book this wrap
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-8">The Service</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => (
            <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}
              className="p-7 rounded-2xl bg-card border border-primary/20 neon-glow" data-testid={`wrap-service-${i}`}>
              <Lightning weight="duotone" size={24} className="neon-text mb-4" />
              <h3 className="font-modern text-xl mb-2">{s.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Inquiry */}
      <section ref={inquiryRef} id="inquiry" className="max-w-3xl mx-auto px-5 sm:px-8 pb-28">
        <div className="p-8 sm:p-10 rounded-2xl bg-card border border-primary/30 neon-glow">
          {done ? (
            <div className="text-center py-8" data-testid="wrap-success">
              <CheckCircle weight="fill" size={56} className="neon-text mx-auto mb-5" />
              <h3 className="text-2xl font-modern mb-2">Inquiry received</h3>
              <p className="text-muted-foreground">The Neon Wigglez Wrap crew will be in touch shortly.</p>
            </div>
          ) : (
            <>
              <p className="overline neon-text mb-2">Get a quote</p>
              <h3 className="text-2xl sm:text-3xl font-modern mb-6">Tell us about your build</h3>
              <form onSubmit={submit} className="space-y-4" data-testid="wrap-form">
                <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-transparent border border-white/20 rounded-lg px-4 py-3 outline-none focus:border-primary transition-colors" data-testid="wrap-name" />
                <input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-transparent border border-white/20 rounded-lg px-4 py-3 outline-none focus:border-primary transition-colors" data-testid="wrap-email" />
                <input placeholder="Vehicle (make / model / year)" value={form.vehicle} onChange={(e) => setForm({ ...form, vehicle: e.target.value })}
                  className="w-full bg-transparent border border-white/20 rounded-lg px-4 py-3 outline-none focus:border-primary transition-colors" data-testid="wrap-vehicle" />
                <textarea required rows={4} placeholder="Describe the wrap you want…" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-transparent border border-white/20 rounded-lg px-4 py-3 outline-none focus:border-primary transition-colors resize-none" data-testid="wrap-message" />
                <button disabled={loading} className="w-full bg-primary text-primary-foreground py-3.5 rounded-full font-modern font-medium hover:bg-primary/90 transition-colors disabled:opacity-60" data-testid="wrap-submit">
                  {loading ? "Sending…" : "Submit inquiry"}
                </button>
              </form>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
